// Video player and gallery logic with audio/mute state management
// Custom terminal-aesthetic video player with full controls
// Handles browser autoplay policy (muted by default, unmute on user interaction)
import { glitch, glitchBurst, glitchRGBSplit } from './effects';
import { prefersReducedMotion } from './animations';
import { showSignalLost } from '../components/SignalLost';

const AUDIO_PREF_KEY = 'chopras-audio-enabled';
const VOLUME_PREF_KEY = 'chopras-volume';

interface VideoData {
  id: string;
  name: string;
  recovered: string;
  duration: string;
}

// Intersection Observer for lazy loading poster images
let lazyLoadObserver: IntersectionObserver | null = null;

/**
 * Initialize lazy loading observer for poster images
 */
function initLazyLoadObserver(): void {
  if (lazyLoadObserver) return;

  lazyLoadObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const thumbnail = entry.target as HTMLElement;
          const img = thumbnail.querySelector('.video-poster-img') as HTMLImageElement;

          if (img && img.dataset.src) {
            img.src = img.dataset.src;
            delete img.dataset.src;
            img.onload = () => {
              thumbnail.classList.add('loaded');
            };
            img.onerror = () => {
              // Keep showing the static/glitch placeholder on error
              thumbnail.classList.add('poster-error');
            };
          }

          lazyLoadObserver?.unobserve(entry.target);
        }
      });
    },
    {
      root: null,
      rootMargin: '100px', // Load images 100px before they enter viewport
      threshold: 0.1
    }
  );
}

/**
 * Cleanup lazy load observer
 */
function cleanupLazyLoadObserver(): void {
  if (lazyLoadObserver) {
    lazyLoadObserver.disconnect();
    lazyLoadObserver = null;
  }
}

/**
 * Audio State Manager
 * Handles mute state, browser autoplay policy compliance, and user preferences
 */
class AudioStateManager {
  private isMuted = true;
  private hasUserInteracted = false;
  private userPrefersSoundEnabled = false;
  private volume = 0.8;

  constructor() {
    this.loadPreference();
  }

  /**
   * Load user's audio preference from localStorage
   * Note: We always start muted for autoplay policy compliance
   */
  private loadPreference(): void {
    const saved = localStorage.getItem(AUDIO_PREF_KEY);
    this.userPrefersSoundEnabled = saved === 'true';

    const savedVolume = localStorage.getItem(VOLUME_PREF_KEY);
    if (savedVolume) {
      this.volume = parseFloat(savedVolume);
    }

    // Always start muted - browser autoplay policy requires it
    this.isMuted = true;
  }

  /**
   * Save audio preference to localStorage
   */
  private savePreference(): void {
    localStorage.setItem(AUDIO_PREF_KEY, String(!this.isMuted));
    localStorage.setItem(VOLUME_PREF_KEY, String(this.volume));
  }

  /**
   * Record that user has interacted with the page
   * If they previously preferred sound enabled, unmute now
   */
  recordUserInteraction(): boolean {
    if (this.hasUserInteracted) return false;

    this.hasUserInteracted = true;

    // If user previously preferred sound, enable it now that they've interacted
    if (this.userPrefersSoundEnabled) {
      this.isMuted = false;
      return true; // Indicates mute state changed
    }
    return false;
  }

  /**
   * Toggle mute state
   */
  toggle(): void {
    // Mark as interacted when toggling
    this.hasUserInteracted = true;
    this.isMuted = !this.isMuted;
    this.savePreference();
  }

  /**
   * Get current mute state
   */
  getMuted(): boolean {
    return this.isMuted;
  }

  /**
   * Check if user has interacted
   */
  getHasInteracted(): boolean {
    return this.hasUserInteracted;
  }

  /**
   * Get current volume
   */
  getVolume(): number {
    return this.volume;
  }

  /**
   * Set volume (0-1)
   */
  setVolume(vol: number): void {
    this.volume = Math.max(0, Math.min(1, vol));
    this.hasUserInteracted = true;
    this.savePreference();
  }
}

// Singleton audio state manager
const audioState = new AudioStateManager();

let currentPlayer: HTMLVideoElement | null = null;
let playerModal: HTMLElement | null = null;
let muteButton: HTMLButtonElement | null = null;
let progressBar: HTMLElement | null = null;
let timeDisplay: HTMLElement | null = null;
let playPauseBtn: HTMLButtonElement | null = null;
let volumeSlider: HTMLInputElement | null = null;
let controlsHideTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Initialize video gallery with keyboard navigation and lazy loading
 */
export function initVideoGallery(container: HTMLElement, videos: VideoData[]): void {
  const listContainer = container.querySelector('.videos-list');
  if (!listContainer) return;

  // Initialize lazy loading observer
  initLazyLoadObserver();

  const items: HTMLElement[] = [];

  videos.forEach((video, index) => {
    const item = createVideoItem(video, index + 1, items);
    items.push(item);
    listContainer.appendChild(item);

    // Observe the thumbnail for lazy loading
    const thumbnail = item.querySelector('.video-thumbnail');
    if (thumbnail && lazyLoadObserver) {
      lazyLoadObserver.observe(thumbnail);
    }
  });
}

/**
 * Create a video list item element with arrow key navigation and poster thumbnail
 */
function createVideoItem(video: VideoData, index: number, allItems: HTMLElement[]): HTMLElement {
  const item = document.createElement('div');
  item.className = 'video-item';
  item.setAttribute('tabindex', '0');
  item.setAttribute('role', 'button');
  item.setAttribute('aria-label', `Play video ${video.name}`);
  item.dataset.videoIndex = String(index - 1); // Store 0-based index for navigation

  const posterPath = `/assets/videos/${video.id}-poster.jpg`;

  item.innerHTML = `
    <div class="video-thumbnail" aria-hidden="true">
      <img
        class="video-poster-img"
        data-src="${posterPath}"
        alt=""
        loading="lazy"
      />
      <div class="video-thumbnail-overlay">
        <span class="thumbnail-play-icon">&#9654;</span>
      </div>
      <div class="video-thumbnail-static"></div>
      <div class="video-thumbnail-scanlines"></div>
    </div>
    <div class="video-info">
      <div class="video-info-header">
        <span class="video-index">[${String(index).padStart(2, '0')}]</span>
        <span class="video-name">${video.name}</span>
        <span class="video-play">&#9654; PLAY</span>
      </div>
      <div class="video-meta">recovered: ${video.recovered} | duration: ${video.duration}</div>
      <div class="video-bar"></div>
    </div>
  `;

  item.addEventListener('click', () => openVideoPlayer(video));
  item.addEventListener('keydown', (e: KeyboardEvent) => {
    // Enter or Space to activate
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openVideoPlayer(video);
      return;
    }

    // Arrow key navigation
    const currentIndex = parseInt(item.dataset.videoIndex || '0', 10);
    const itemCount = allItems.length;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        // Move to next item, or wrap to first
        if (currentIndex < itemCount - 1) {
          allItems[currentIndex + 1].focus();
        } else {
          allItems[0].focus();
        }
        break;

      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        // Move to previous item, or wrap to last
        if (currentIndex > 0) {
          allItems[currentIndex - 1].focus();
        } else {
          allItems[itemCount - 1].focus();
        }
        break;

      case 'Home':
        e.preventDefault();
        // Focus first item
        if (allItems.length > 0) {
          allItems[0].focus();
        }
        break;

      case 'End':
        e.preventDefault();
        // Focus last item
        if (allItems.length > 0) {
          allItems[allItems.length - 1].focus();
        }
        break;
    }
  });

  return item;
}

/**
 * Announce a message to screen readers via live region
 */
function announceToScreenReader(message: string): void {
  const srStatus = playerModal?.querySelector('.video-sr-status');
  if (srStatus) {
    srStatus.textContent = message;
    // Clear after a delay to allow re-announcement of same message
    setTimeout(() => {
      if (srStatus.textContent === message) {
        srStatus.textContent = '';
      }
    }, 1000);
  }
}

/**
 * Format time in terminal style (MM:SS or HH:MM:SS)
 */
function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) {
    return '??:??';
  }

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hrs > 0) {
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * Create the terminal-style progress bar
 */
function createProgressBar(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'video-progress-container';

  container.innerHTML = `
    <div class="video-progress-track" role="slider" aria-label="Video progress" tabindex="0"
         aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" aria-valuetext="0:00 of 0:00">
      <div class="video-progress-fill"></div>
      <div class="video-progress-handle"></div>
    </div>
  `;

  const track = container.querySelector('.video-progress-track') as HTMLElement;

  // Click to seek
  track.addEventListener('click', (e) => {
    if (!currentPlayer) return;
    const rect = track.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    currentPlayer.currentTime = percent * currentPlayer.duration;
    handleVideoInteraction();
  });

  // Keyboard controls for progress
  track.addEventListener('keydown', (e) => {
    if (!currentPlayer) return;
    if (e.key === 'ArrowLeft') {
      currentPlayer.currentTime = Math.max(0, currentPlayer.currentTime - 5);
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      currentPlayer.currentTime = Math.min(currentPlayer.duration, currentPlayer.currentTime + 5);
      e.preventDefault();
    }
  });

  // Drag to seek
  let isDragging = false;

  track.addEventListener('mousedown', (e) => {
    isDragging = true;
    e.preventDefault();
  });

  document.addEventListener('mousemove', (e) => {
    if (!isDragging || !currentPlayer) return;
    const rect = track.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    currentPlayer.currentTime = percent * currentPlayer.duration;
  });

  document.addEventListener('mouseup', () => {
    if (isDragging) {
      isDragging = false;
      handleVideoInteraction();
    }
  });

  return container;
}

/**
 * Update progress bar position
 */
function updateProgress(): void {
  if (!currentPlayer || !progressBar) return;

  const fill = progressBar.querySelector('.video-progress-fill') as HTMLElement;
  const handle = progressBar.querySelector('.video-progress-handle') as HTMLElement;
  const track = progressBar.querySelector('.video-progress-track') as HTMLElement;

  if (fill && handle && currentPlayer.duration) {
    const percent = (currentPlayer.currentTime / currentPlayer.duration) * 100;
    fill.style.width = `${percent}%`;
    handle.style.left = `${percent}%`;

    // Update ARIA attributes for screen readers
    if (track) {
      const elapsed = formatTime(currentPlayer.currentTime);
      const total = formatTime(currentPlayer.duration);
      track.setAttribute('aria-valuenow', String(Math.round(percent)));
      track.setAttribute('aria-valuetext', `${elapsed} of ${total}`);
    }
  }

  // Update time display
  if (timeDisplay && currentPlayer.duration) {
    const elapsed = formatTime(currentPlayer.currentTime);
    const total = formatTime(currentPlayer.duration);
    timeDisplay.innerHTML = `<span class="time-elapsed">${elapsed}</span> / <span class="time-total">${total}</span>`;
  }
}

/**
 * Create play/pause button
 */
function createPlayPauseButton(): HTMLButtonElement {
  const button = document.createElement('button');
  button.className = 'video-play-pause-btn';
  button.setAttribute('type', 'button');
  updatePlayPauseUI(button, false);

  button.addEventListener('click', (e) => {
    e.stopPropagation();
    togglePlayPause();
  });

  return button;
}

/**
 * Update play/pause button UI
 */
function updatePlayPauseUI(button: HTMLButtonElement, isPlaying: boolean): void {
  button.innerHTML = `
    <span class="pp-icon" aria-hidden="true">${isPlaying ? '&#10074;&#10074;' : '&#9654;'}</span>
    <span class="pp-text">${isPlaying ? 'PAUSE' : 'PLAY'}</span>
  `;
  button.classList.toggle('is-playing', isPlaying);
  button.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
  button.setAttribute('aria-pressed', String(isPlaying));
}

/**
 * Apply glitch transition effect on play/pause
 * Uses a combination of burst and RGB split for dramatic effect
 */
function applyPlayPauseGlitch(): void {
  if (prefersReducedMotion() || !playerModal) return;

  const videoWrapper = playerModal.querySelector('.video-wrapper') as HTMLElement;
  if (!videoWrapper) return;

  // Apply quick glitch burst to the video wrapper
  glitchBurst(videoWrapper, 2, 5);

  // Also apply RGB split to the control bar for unified effect
  const controlBar = playerModal.querySelector('.video-controls-bar') as HTMLElement;
  if (controlBar) {
    glitchRGBSplit(controlBar, 3, 0.15);
  }
}

/**
 * Toggle play/pause state with glitch transition
 */
function togglePlayPause(): void {
  if (!currentPlayer) return;

  handleVideoInteraction();

  // Apply glitch effect on state change
  applyPlayPauseGlitch();

  if (currentPlayer.paused) {
    currentPlayer.play();
    announceToScreenReader('Video playing');
  } else {
    currentPlayer.pause();
    announceToScreenReader('Video paused');
  }
}

/**
 * Create time display element
 */
function createTimeDisplay(): HTMLElement {
  const display = document.createElement('div');
  display.className = 'video-time-display';
  display.innerHTML = '<span class="time-elapsed">??:??</span> / <span class="time-total">??:??</span>';
  return display;
}

/**
 * Create mute toggle button UI
 */
function createMuteToggle(): HTMLButtonElement {
  const button = document.createElement('button');
  button.className = 'video-mute-toggle';
  button.setAttribute('type', 'button');
  updateMuteButtonUI(button);

  button.addEventListener('click', (e) => {
    e.stopPropagation();
    audioState.toggle();
    if (currentPlayer) {
      currentPlayer.muted = audioState.getMuted();
    }
    updateMuteButtonUI(button);
  });

  return button;
}

/**
 * Update mute button visual state
 */
function updateMuteButtonUI(button: HTMLButtonElement): void {
  const isMuted = audioState.getMuted();
  button.innerHTML = `
    <span class="mute-icon" aria-hidden="true">[${isMuted ? 'X' : '#'}]</span>
    <span class="mute-text">${isMuted ? 'UNMUTE' : 'MUTE'}</span>
  `;
  button.setAttribute('aria-label', isMuted ? 'Unmute audio' : 'Mute audio');
  button.setAttribute('aria-pressed', String(!isMuted));
  button.classList.toggle('is-muted', isMuted);
}

/**
 * Create volume slider
 */
function createVolumeSlider(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'video-volume-container';

  const volumePercent = Math.round(audioState.getVolume() * 100);
  container.innerHTML = `
    <span class="volume-label" id="volume-label">VOL</span>
    <input type="range" class="video-volume-slider" min="0" max="100" value="${volumePercent}"
           aria-label="Volume" aria-valuetext="${volumePercent}%" aria-labelledby="volume-label" />
    <span class="volume-value" aria-hidden="true">${volumePercent}%</span>
  `;

  const slider = container.querySelector('.video-volume-slider') as HTMLInputElement;
  const valueDisplay = container.querySelector('.volume-value') as HTMLElement;

  slider.addEventListener('input', (e) => {
    const value = parseInt((e.target as HTMLInputElement).value) / 100;
    audioState.setVolume(value);
    if (currentPlayer) {
      currentPlayer.volume = value;
    }
    const percentText = `${Math.round(value * 100)}%`;
    valueDisplay.textContent = percentText;
    slider.setAttribute('aria-valuetext', percentText);
    handleVideoInteraction();
  });

  return container;
}

/**
 * Create audio hint for first-time users
 */
function createAudioHint(): HTMLElement {
  const hint = document.createElement('div');
  hint.className = 'video-audio-hint';
  hint.textContent = '> Click to enable sound';

  if (audioState.getHasInteracted()) {
    hint.classList.add('hidden');
  }

  return hint;
}

/**
 * Show/hide controls with fade
 */
function showControls(): void {
  const controls = playerModal?.querySelector('.video-controls-bar');
  const header = playerModal?.querySelector('.video-player-header');

  if (controls) {
    controls.classList.remove('hidden');
    controls.classList.add('visible');
  }
  if (header) {
    header.classList.remove('hidden');
    header.classList.add('visible');
  }

  // Reset hide timer
  if (controlsHideTimeout) {
    clearTimeout(controlsHideTimeout);
  }

  // Auto-hide after 3 seconds if playing
  if (currentPlayer && !currentPlayer.paused) {
    controlsHideTimeout = setTimeout(hideControls, 3000);
  }
}

function hideControls(): void {
  if (!currentPlayer || currentPlayer.paused) return;

  const controls = playerModal?.querySelector('.video-controls-bar');
  const header = playerModal?.querySelector('.video-player-header');

  if (controls) {
    controls.classList.add('hidden');
    controls.classList.remove('visible');
  }
  if (header) {
    header.classList.add('hidden');
    header.classList.remove('visible');
  }
}

/**
 * Open video in fullscreen modal player with terminal-styled controls
 */
function openVideoPlayer(video: VideoData): void {
  // Close existing player if open
  closeVideoPlayer();

  playerModal = document.createElement('div');
  playerModal.className = 'video-player';
  playerModal.setAttribute('role', 'dialog');
  playerModal.setAttribute('aria-modal', 'true');
  playerModal.setAttribute('aria-label', `Video player: ${video.name}`);

  // Create video wrapper for controls overlay
  const videoWrapper = document.createElement('div');
  videoWrapper.className = 'video-wrapper';

  currentPlayer = document.createElement('video');
  currentPlayer.className = 'video-element';
  currentPlayer.controls = false;
  currentPlayer.autoplay = true;
  currentPlayer.loop = false; // Don't use native loop - we handle looping manually to show signal lost screen
  currentPlayer.playsInline = true;
  currentPlayer.muted = true; // Start muted for autoplay
  currentPlayer.volume = audioState.getVolume();

  // Try WebM first, fallback to MP4
  const sourceWebm = document.createElement('source');
  sourceWebm.src = `/assets/videos/${video.id}.webm`;
  sourceWebm.type = 'video/webm';

  const sourceMp4 = document.createElement('source');
  sourceMp4.src = `/assets/videos/${video.id}.mp4`;
  sourceMp4.type = 'video/mp4';

  currentPlayer.appendChild(sourceWebm);
  currentPlayer.appendChild(sourceMp4);

  // Add poster
  currentPlayer.poster = `/assets/videos/${video.id}-poster.jpg`;

  // Handle autoplay - unmute after it starts since user already clicked to open
  currentPlayer.play().then(() => {
    // Autoplay succeeded - unmute now since user interaction (clicking to open) already happened
    if (currentPlayer) {
      currentPlayer.muted = false;
      // Update audio state to reflect unmuted
      if (audioState.getMuted()) {
        audioState.toggle();
      }
      audioState.recordUserInteraction();
      if (muteButton) {
        updateMuteButtonUI(muteButton);
      }
    }
  }).catch(() => {
    showPlayOverlay(videoWrapper, video);
  });

  // Create scanline overlay
  const scanlines = document.createElement('div');
  scanlines.className = 'video-scanlines-overlay';

  // Create header with title
  const header = document.createElement('div');
  header.className = 'video-player-header visible';
  header.innerHTML = `
    <div class="video-player-title">
      <span class="title-prompt">&gt;</span> PLAYING: <span class="title-name">${video.name}</span>
      <span class="title-blink">_</span>
    </div>
  `;

  // Create close button
  const closeButton = document.createElement('button');
  closeButton.className = 'video-player-close';
  closeButton.setAttribute('type', 'button');
  closeButton.setAttribute('aria-label', 'Close video player (Escape key)');
  closeButton.innerHTML = '<span aria-hidden="true">&gt; CLOSE</span> <span class="key-hint" aria-hidden="true">[ESC]</span>';
  closeButton.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeVideoPlayer();
  });
  header.appendChild(closeButton);

  // Create bottom control bar
  const controlBar = document.createElement('div');
  controlBar.className = 'video-controls-bar visible';

  // Left controls: play/pause, time
  const leftControls = document.createElement('div');
  leftControls.className = 'controls-left';

  playPauseBtn = createPlayPauseButton();
  leftControls.appendChild(playPauseBtn);

  timeDisplay = createTimeDisplay();
  leftControls.appendChild(timeDisplay);

  // Center: progress bar
  progressBar = createProgressBar();

  // Right controls: volume, mute
  const rightControls = document.createElement('div');
  rightControls.className = 'controls-right';

  const volumeContainer = createVolumeSlider();
  volumeSlider = volumeContainer.querySelector('.video-volume-slider') as HTMLInputElement;
  rightControls.appendChild(volumeContainer);

  muteButton = createMuteToggle();
  rightControls.appendChild(muteButton);

  // Audio hint
  const audioHint = createAudioHint();
  rightControls.appendChild(audioHint);

  controlBar.appendChild(leftControls);
  controlBar.appendChild(progressBar);
  controlBar.appendChild(rightControls);

  // Handle click on video to toggle play/pause
  currentPlayer.addEventListener('click', (e) => {
    e.stopPropagation();
    togglePlayPause();
  });

  // Update progress bar as video plays
  currentPlayer.addEventListener('timeupdate', updateProgress);
  currentPlayer.addEventListener('loadedmetadata', updateProgress);

  // Update play/pause button on state change
  currentPlayer.addEventListener('play', () => {
    if (playPauseBtn) updatePlayPauseUI(playPauseBtn, true);
    showControls();
  });

  currentPlayer.addEventListener('pause', () => {
    if (playPauseBtn) updatePlayPauseUI(playPauseBtn, false);
    showControls();
  });

  // Create screen reader status announcer
  const srStatus = document.createElement('div');
  srStatus.className = 'sr-only video-sr-status';
  srStatus.setAttribute('role', 'status');
  srStatus.setAttribute('aria-live', 'polite');
  srStatus.setAttribute('aria-atomic', 'true');

  // Assemble player
  videoWrapper.appendChild(currentPlayer);
  videoWrapper.appendChild(scanlines);
  playerModal.appendChild(header);
  playerModal.appendChild(videoWrapper);
  playerModal.appendChild(controlBar);
  playerModal.appendChild(srStatus);

  // Apply glitch effect on open
  document.body.appendChild(playerModal);
  if (!prefersReducedMotion()) {
    glitch(playerModal);
  }

  // Close on escape, add keyboard controls
  document.addEventListener('keydown', handleKeyboard);

  // Handle video end - show "signal lost" briefly
  currentPlayer.addEventListener('ended', handleVideoEnd);

  // Close when clicking backdrop
  playerModal.addEventListener('click', (e) => {
    if (e.target === playerModal) {
      closeVideoPlayer();
    }
  });

  // Show/hide controls on mouse movement
  playerModal.addEventListener('mousemove', showControls);

  // Focus player for keyboard controls
  playerModal.setAttribute('tabindex', '-1');
  playerModal.focus();
}

/**
 * Toggle fullscreen mode with cross-browser support
 * Handles Safari webkit prefix and other browser variations
 */
function toggleFullscreen(): void {
  if (!playerModal) return;

  // Type declarations for vendor-prefixed fullscreen APIs
  interface ExtendedDocument extends Document {
    webkitFullscreenElement?: Element | null;
    mozFullScreenElement?: Element | null;
    msFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void>;
    mozCancelFullScreen?: () => Promise<void>;
    msExitFullscreen?: () => Promise<void>;
  }

  interface ExtendedElement extends HTMLElement {
    webkitRequestFullscreen?: () => Promise<void>;
    mozRequestFullScreen?: () => Promise<void>;
    msRequestFullscreen?: () => Promise<void>;
  }

  const doc = document as ExtendedDocument;
  const elem = playerModal as ExtendedElement;

  // Check if currently in fullscreen
  const isFullscreen = !!(
    document.fullscreenElement ||
    doc.webkitFullscreenElement ||
    doc.mozFullScreenElement ||
    doc.msFullscreenElement
  );

  if (isFullscreen) {
    // Exit fullscreen
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (doc.webkitExitFullscreen) {
      doc.webkitExitFullscreen();
    } else if (doc.mozCancelFullScreen) {
      doc.mozCancelFullScreen();
    } else if (doc.msExitFullscreen) {
      doc.msExitFullscreen();
    }
  } else {
    // Enter fullscreen
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  }
}

/**
 * Handle user interaction with video - unmute on first interaction
 */
function handleVideoInteraction(): void {
  const wasFirstInteraction = !audioState.getHasInteracted();
  audioState.recordUserInteraction();

  // On first interaction, automatically unmute for better UX
  if (wasFirstInteraction && currentPlayer) {
    // Directly unmute - don't toggle, just set to unmuted
    if (audioState.getMuted()) {
      audioState.toggle();
    }
    currentPlayer.muted = false;
    if (muteButton) {
      updateMuteButtonUI(muteButton);
    }
  }

  // Hide audio hint
  const hint = document.querySelector('.video-audio-hint');
  if (hint) {
    hint.classList.add('hidden');
  }
}

/**
 * Show play overlay when autoplay fails
 */
function showPlayOverlay(wrapper: HTMLElement, video: VideoData): void {
  const overlay = document.createElement('div');
  overlay.className = 'video-play-overlay';
  overlay.innerHTML = `
    <div class="play-overlay-content">
      <div class="play-overlay-text">&gt; AUTOPLAY BLOCKED</div>
      <button class="video-play-btn" aria-label="Play ${video.name}">
        &#9654; INITIALIZE PLAYBACK
      </button>
    </div>
  `;

  overlay.addEventListener('click', (e) => {
    e.stopPropagation();
    if (currentPlayer) {
      currentPlayer.play();
      overlay.remove();
      handleVideoInteraction();
    }
  });

  wrapper.appendChild(overlay);
}

/**
 * Close the video player modal
 */
function closeVideoPlayer(): void {
  if (controlsHideTimeout) {
    clearTimeout(controlsHideTimeout);
  }

  if (playerModal) {
    if (currentPlayer) {
      currentPlayer.pause();
    }

    if (!prefersReducedMotion()) {
      glitch(playerModal);
    }

    setTimeout(() => {
      playerModal?.remove();
      playerModal = null;
      currentPlayer = null;
      muteButton = null;
      progressBar = null;
      timeDisplay = null;
      playPauseBtn = null;
      volumeSlider = null;
    }, prefersReducedMotion() ? 0 : 200);
  }
  document.removeEventListener('keydown', handleKeyboard);
}

/**
 * Handle keyboard controls for video player
 */
function handleKeyboard(e: KeyboardEvent): void {
  if (!currentPlayer) return;

  switch (e.key) {
    case 'Escape':
      closeVideoPlayer();
      break;
    case ' ':
      e.preventDefault();
      togglePlayPause();
      break;
    case 'm':
    case 'M':
      audioState.toggle();
      currentPlayer.muted = audioState.getMuted();
      if (muteButton) updateMuteButtonUI(muteButton);
      handleVideoInteraction();
      break;
    case 'ArrowLeft':
      currentPlayer.currentTime = Math.max(0, currentPlayer.currentTime - 5);
      showControls();
      break;
    case 'ArrowRight':
      currentPlayer.currentTime = Math.min(currentPlayer.duration, currentPlayer.currentTime + 5);
      showControls();
      break;
    case 'ArrowUp':
      e.preventDefault();
      const newVolUp = Math.min(1, audioState.getVolume() + 0.1);
      audioState.setVolume(newVolUp);
      currentPlayer.volume = newVolUp;
      if (volumeSlider) {
        volumeSlider.value = String(newVolUp * 100);
        const valueDisplay = volumeSlider.parentElement?.querySelector('.volume-value');
        if (valueDisplay) valueDisplay.textContent = `${Math.round(newVolUp * 100)}%`;
      }
      showControls();
      break;
    case 'ArrowDown':
      e.preventDefault();
      const newVolDown = Math.max(0, audioState.getVolume() - 0.1);
      audioState.setVolume(newVolDown);
      currentPlayer.volume = newVolDown;
      if (volumeSlider) {
        volumeSlider.value = String(newVolDown * 100);
        const valueDisplay = volumeSlider.parentElement?.querySelector('.volume-value');
        if (valueDisplay) valueDisplay.textContent = `${Math.round(newVolDown * 100)}%`;
      }
      showControls();
      break;
    case 'f':
    case 'F':
      toggleFullscreen();
      break;
  }
}

function handleVideoEnd(): void {
  if (!currentPlayer || !playerModal) return;

  const wrapper = playerModal.querySelector('.video-wrapper') as HTMLElement;
  if (!wrapper) return;

  // Show the enhanced signal lost screen with glitch effects
  showSignalLost(wrapper, {
    duration: 2000,
    primaryMessage: '> SIGNAL LOST',
    secondaryMessage: 'RECONNECTING...',
    crypticMessages: [
      'BUFFER OVERFLOW',
      'FRAME CORRUPTION',
      'VECTOR MISALIGNMENT',
      '??:??:??',
      'TEMPORAL ANOMALY',
      'NODE 7 OFFLINE',
    ],
    onComplete: () => {
      currentPlayer?.play();
    },
  });
}

/**
 * Cleanup video gallery
 */
export function cleanupVideoGallery(): void {
  closeVideoPlayer();
  cleanupLazyLoadObserver();
}

/**
 * Get current audio state (for external access if needed)
 */
export function getAudioState(): { muted: boolean; hasInteracted: boolean; volume: number } {
  return {
    muted: audioState.getMuted(),
    hasInteracted: audioState.getHasInteracted(),
    volume: audioState.getVolume()
  };
}
